import { Button } from "@/components/UI/Button";

import Toggles from "@/components/UI/Toggles";
import Tooltip from "@/components/UI/Tooltip";
import NotificationGroupColumn from "./NotificationGroupColumn";
import { notifyUser } from "@/lib/browserNotifications";
import useNotificationPreferencesStore from "@/store/notifications/notificationPreferencesStore";
import featureFlagStore from "@/store/featureFlagStore";
import {
  NOTIFICATION_GROUPS,
  type NotificationGroupKey,
} from "@/lib/notificationConfig";
import { useEffect, useRef, useState } from "react";

type MasterChannel = "sound" | "tray" | "browser" | null;

const NotificationsIndex = () => {
  // ─── Browser Notification State ──────────────────────────────────────────
  async function enableNotifications() {
    notifyUser();
    updateBrowserDenied();
  }

  const [isBrowserDenied, setIsBrowserDenied] = useState(
    Notification.permission === "denied",
  );
  const [isBrowserGranted, setIsBrowserGranted] = useState(
    Notification.permission === "granted",
  );
  const [flashErrorMessageOrange, setFlashErrorMessageOrange] = useState(false);
  const [showMasterBrowserWarning, setShowMasterBrowserWarning] =
    useState(false);

  useEffect(() => {
    if (flashErrorMessageOrange) {
      setTimeout(() => {
        setFlashErrorMessageOrange(false);
      }, 250);
    }
  }, [flashErrorMessageOrange]);

  function updateBrowserDenied() {
    if (Notification.permission === "denied") {
      setIsBrowserDenied(true);
      setFlashErrorMessageOrange(true);
    } else if (Notification.permission === "granted") {
      setIsBrowserGranted(true);
    }
  }

  const subscribeDeniedMessage =
    "Subscription to notifications blocked, please check your browser settings.";

  // ─── Notification Preferences ────────────────────────────────────────────
  const globalSoundEnabled = useNotificationPreferencesStore(
    (state) => state.globalSoundEnabled,
  );
  const globalBellEnabled = useNotificationPreferencesStore(
    (state) => state.globalBellEnabled,
  );
  const globalBrowserEnabled = useNotificationPreferencesStore(
    (state) => state.globalBrowserEnabled,
  );
  const groupSounds = useNotificationPreferencesStore(
    (state) => state.groupSounds,
  );
  const statusPreferences = useNotificationPreferencesStore(
    (state) => state.statusPreferences,
  );
  const setGlobalSoundEnabled = useNotificationPreferencesStore(
    (state) => state.setGlobalSoundEnabled,
  );
  const setGlobalBellEnabled = useNotificationPreferencesStore(
    (state) => state.setGlobalBellEnabled,
  );
  const setGlobalBrowserEnabled = useNotificationPreferencesStore(
    (state) => state.setGlobalBrowserEnabled,
  );
  const setGroupSound = useNotificationPreferencesStore(
    (state) => state.setGroupSound,
  );
  const toggleStatusChannel = useNotificationPreferencesStore(
    (state) => state.toggleStatusChannel,
  );
  const toggleAllInGroup = useNotificationPreferencesStore(
    (state) => state.toggleAllInGroup,
  );
  const resetToDefaults = useNotificationPreferencesStore(
    (state) => state.resetToDefaults,
  );

  // ─── Feature-flag-gated negotiation column (LQC-637 / LQC-1745) ──────────
  // The negotiation group only makes sense when the Request Information feature
  // is live; it's stacked under the "Other" column and hidden otherwise.
  const rpRequestInformationButtons = featureFlagStore(
    (state) => state.rpRequestInformationButtons,
  );

  // Render one notification group column. Shared so "Other" and "Negotiation"
  // can be stacked in the same third slot without duplicating the prop wiring.
  const renderColumn = (groupKey: NotificationGroupKey) => {
    const group = NOTIFICATION_GROUPS[groupKey];
    return (
      <NotificationGroupColumn
        key={groupKey}
        groupKey={groupKey}
        label={group.label}
        statuses={group.statuses}
        groupSound={groupSounds[groupKey]}
        statusPreferences={statusPreferences}
        globalSoundDisabled={!globalSoundEnabled}
        globalBellDisabled={!globalBellEnabled}
        globalBrowserDisabled={!globalBrowserEnabled}
        onHighlightMaster={setHighlightedMaster}
        onDisabledToggleClick={handleDisabledToggleClick}
        onGroupSoundChange={(soundFile) => setGroupSound(groupKey, soundFile)}
        onStatusToggle={(status, channel) =>
          toggleStatusChannel(status, channel)
        }
        onToggleAll={(channel, enabled) =>
          toggleAllInGroup(groupKey, channel, enabled, group.statuses)
        }
      />
    );
  };

  // ─── Master Toggle Pulse Highlight ─────────────────────────────────────
  const [highlightedMaster, setHighlightedMaster] =
    useState<MasterChannel>(null);
  const masterRef = useRef<HTMLDivElement | null>(null);
  const disabledClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    return () => {
      if (disabledClickTimeoutRef.current) {
        clearTimeout(disabledClickTimeoutRef.current);
      }
    };
  }, []);

  // When the user clicks a disabled status/toggle-all toggle: scroll the
  // master controls into view and pulse the relevant master toggle so the
  // user can see they need to enable it at the top.
  const handleDisabledToggleClick = (channel: MasterChannel) => {
    masterRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedMaster(channel);
    if (disabledClickTimeoutRef.current) {
      clearTimeout(disabledClickTimeoutRef.current);
    }
    disabledClickTimeoutRef.current = setTimeout(() => {
      setHighlightedMaster(null);
    }, 1800);
  };

  return (
    <div
      data-testid="NotificationsIndexContainer"
      id="NotificationsIndexContainer"
      className="flex w-full flex-col space-y-6"
    >
      {/* Info Banner */}
      {/* <InfoBanner title="Notification Preferences">
        Preferences are saved locally to your browser. Changes take effect
        immediately.
      </InfoBanner> */}

      {/* ─── Status Notifications ─────────────────────────────────────────── */}
      <div className="space-y-2">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
            />
          </svg>
          Status Notifications
        </h3>

        {/* ─── Master Controls Row ─────────────────────────────────────── */}
        <div
          ref={masterRef}
          className="ml-8 flex flex-col rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800"
        >
          {/* Stack description above toggles below 1400px so nothing clips. */}
          <div className="flex flex-col gap-3 min-[1400px]:flex-row min-[1400px]:items-center min-[1400px]:justify-between">
            <div className="flex flex-col">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Master / Mute
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Enable or disable all notifications by channel
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Can be used to quickly mute a notification channel while keeping
                the current settings configuration
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 min-[1400px]:flex-nowrap min-[1400px]:justify-start">
              <div className="flex w-24 flex-col items-center gap-1">
                <Tooltip
                  text="Play a sound when a status changes"
                  showIcon={false}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5 cursor-help text-gray-500 dark:text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z"
                    />
                  </svg>
                </Tooltip>
                <Tooltip text="Play a sound when a status changes">
                  <span className="cursor-help text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Sound
                  </span>
                </Tooltip>
                <Toggles
                  checked={globalSoundEnabled}
                  handleChange={() =>
                    setGlobalSoundEnabled(!globalSoundEnabled)
                  }
                  highlight={highlightedMaster === "sound"}
                  highlightSize="wide"
                />
              </div>
              <div className="flex w-24 flex-col items-center gap-1">
                <Tooltip
                  text="Show alerts in the notifications panel"
                  showIcon={false}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5 cursor-help text-gray-500 dark:text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                    />
                  </svg>
                </Tooltip>
                <Tooltip text="Show alerts in the notifications panel">
                  <span className="cursor-help text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Notifications
                  </span>
                </Tooltip>
                <Toggles
                  checked={globalBellEnabled}
                  handleChange={() => setGlobalBellEnabled(!globalBellEnabled)}
                  highlight={highlightedMaster === "tray"}
                  highlightSize="wide"
                />
              </div>
              <div className="flex w-24 flex-col items-center gap-1">
                <Tooltip
                  text="Push notifications via the browser"
                  showIcon={false}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-5 w-5 cursor-help text-gray-500 dark:text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
                    />
                  </svg>
                </Tooltip>
                <Tooltip text="Push notifications via the browser">
                  <span className="cursor-help text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Browser
                  </span>
                </Tooltip>
                <Toggles
                  checked={globalBrowserEnabled}
                  handleChange={async () => {
                    const newValue = !globalBrowserEnabled;
                    setGlobalBrowserEnabled(newValue);
                    if (newValue && Notification.permission !== "granted") {
                      const result = await notifyUser();
                      updateBrowserDenied();
                      if (result !== "granted") {
                        setTimeout(() => {
                          setGlobalBrowserEnabled(false);
                        }, 500);
                        setShowMasterBrowserWarning(true);
                        setTimeout(
                          () => setShowMasterBrowserWarning(false),
                          5000,
                        );
                      }
                    }
                  }}
                  highlight={highlightedMaster === "browser"}
                  highlightSize="wide"
                />
              </div>
            </div>
          </div>
          <div className="flex h-5 items-end justify-end">
            {showMasterBrowserWarning && (
              <span className="text-xs text-orange-500">
                Browser notifications blocked — check browser settings
              </span>
            )}
          </div>
        </div>

        {/* Stack columns below 1400px so the sound settings aren't squished. */}
        <div className="ml-8 flex flex-col gap-3 min-[1400px]:flex-row min-[1400px]:items-start">
          {renderColumn("actionRequired")}
          {renderColumn("waitingCounterparty")}
          {/* "Other" with the Negotiation panel stacked beneath it (LQC-637). */}
          <div className="flex flex-1 flex-col gap-3">
            {renderColumn("noFurtherAction")}
            {rpRequestInformationButtons && renderColumn("tradeSettingsNegotiation")}
          </div>
        </div>

        {/* Reset to Defaults */}
        <div className="ml-8 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Reset All to Defaults
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              Restore all notification preferences to their default settings -
              OFF
            </div>
          </div>
          <div className="ml-4 flex-shrink-0">
            <Button
              variant="sidebarButton"
              className="w-fit px-3 py-1.5 text-xs"
              onClick={resetToDefaults}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* ─── Browser Notifications ────────────────────────────────────────── */}
      <div className="space-y-1">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
            />
          </svg>
          Browser Notifications
        </h3>

        <div className="ml-8 space-y-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-300 pb-3 dark:border-gray-600">
            {isBrowserGranted ? (
              <div className="text-sm text-green-700 dark:text-green-400">
                You are subscribed to browser notifications. To unsubscribe,
                please navigate to browser settings.
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Button
                  variant="sidebarButton"
                  className="w-fit p-2"
                  onClick={() => {
                    enableNotifications();
                  }}
                >
                  Subscribe to Notifications
                </Button>
                <div
                  className={
                    flashErrorMessageOrange
                      ? "text-sm text-[#F97316]"
                      : "text-sm text-black dark:text-white"
                  }
                >
                  {isBrowserDenied && subscribeDeniedMessage}
                </div>
              </div>
            )}
          </div>

          <div className="text-sm text-gray-700 dark:text-gray-300">
            <p className="mb-2">
              Browser notifications allow you to receive real-time alerts about
              trade updates even when the application is minimized or running in
              the background.
            </p>
            <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
              You'll receive notifications for trade status changes based on
              your preferences above. Notifications will only appear when the
              application is open in your browser.
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Note: Browser notifications are controlled by your browser
              settings and your operating system's notification settings. If
              notifications aren't appearing, check both your browser's site
              permissions and your system notification settings (e.g. Windows
              Settings &gt; System &gt; Notifications).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsIndex;
